import type { AxiosError } from "axios";
import { camelCase } from "es-toolkit";

import type { ValidationFailure } from "@/plugins/errors/types";
import {
  getFailureMessage,
  getFailurePropertyName,
} from "@/plugins/errors/types/validationFailure";

type ErrorResponseData = {
  failures?: ValidationFailure[];
  title?: string;
  detail?: string;
  message?: string;
};

export type FormKitErrors = {
  formErrors: string[]; // errors to be shown in the general form level
  fieldErrors: Record<string, string[]>; // errors to be shown in the field level
};

/**
 * Get error message from error object from the backend.
 * If the `failures` array is empty, returns a "Something went wrong" message.
 * @param error
 * @returns {string[]}
 */
export function getErrorMessage(error: unknown) {
  const failureMessages: string[] = [];

  const axiosError = error as AxiosError;
  if (!axiosError) {
    return failureMessages;
  }

  const errorResponseData = axiosError?.response?.data as
    | ErrorResponseData
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((failure) => {
      failureMessages.push(getFailureMessage(failure));
    });
  }

  if (failureMessages.length === 0) {
    const fallbackMessages = [
      errorResponseData?.detail,
      errorResponseData?.title,
      errorResponseData?.message,
      axiosError.message,
    ].filter((message): message is string => !!message?.trim());

    if (fallbackMessages.length > 0) {
      failureMessages.push(...fallbackMessages);
    } else {
      failureMessages.push("Something went wrong");
    }
  }

  return failureMessages;
}

/**
 * Get error messages from error object from the backend, mapped on property name. Will return properties in camelCase
 * @param error
 * @returns {Record<string, string[]>>}
 */
export function getErrorsPerProperty(error: unknown): Record<string, string[]> {
  const failureMessages = {} as Record<string, string[]>;

  const axiosError = error as AxiosError;
  if (!axiosError) {
    return failureMessages;
  }

  const errorResponseData = axiosError?.response?.data as
    | ErrorResponseData
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((f) => {
      const failurePropertyName = getFailurePropertyName(f);

      if (!failurePropertyName) return;

      const propertyName = camelCase(failurePropertyName);
      failureMessages[propertyName] = [
        ...(failureMessages[propertyName] ?? []),
        getFailureMessage(f),
      ];
    });
  }

  return failureMessages;
}

export function getFormKitErrors(error: unknown): FormKitErrors {
  return {
    formErrors: getErrorMessage(error),
    fieldErrors: getErrorsPerProperty(error),
  };
}
